/**
 * publishError — what went wrong, as a value the UI can branch on.
 *
 * The publish/git paths already return errors as values rather than throwing,
 * which is the right instinct. The payload was the problem: `error?: string`,
 * interpolated inside a worker ("push exited 1 (after retries)") and, when
 * absent, replaced by `outcome.log.join("\n")` — raw libgit2 stderr rendered
 * into a small red header. Nothing downstream could tell "your session
 * expired" from "you are out of storage" from "someone else published first",
 * so the UI could never offer the right next step.
 *
 * A tag plus the original text keeps the diagnostics while making the decision
 * machine-readable. `Data.TaggedError` is the monorepo precedent
 * (packages/editor/src/widgets/formSubmit.ts); these are plain data because
 * they cross a `postMessage` boundary, where a class instance would not
 * survive structured cloning.
 */
import { ApiError } from "../api/client.js";
export type PublishErrorTag =
  | "Unauthorized"
  | "PushRejected"
  | "QuotaExceeded"
  | "CloneFailed"
  | "CommitFailed"
  | "AssetSyncFailed"
  | "TimedOut"
  | "InvalidSite"
  | "Unknown";

export interface PublishError {
  readonly _tag: PublishErrorTag;
  /** The original worker text — kept for diagnostics, never the whole story. */
  readonly detail: string;
}

const err = (_tag: PublishErrorTag, detail: string): PublishError => ({
  _tag,
  detail,
});

/** Worker output that may carry a failure. */
export interface RawFailure {
  readonly error?: string | undefined;
  /** The worker's own coarse marker, when it has one. */
  readonly kind?: string | undefined;
  /** git stdout+stderr; the HTTP status usually only appears here. */
  readonly log?: ReadonlyArray<string> | undefined;
}

/**
 * Classify a worker failure. Ordered most-specific first: an explicit marker
 * from the worker beats a status sniffed out of the log, which beats the shape
 * of the git command that failed.
 */
export function classifyPublishError(raw: RawFailure): PublishError {
  const detail = (raw.error ?? "").trim();
  const haystack = `${detail}\n${(raw.log ?? []).join("\n")}`.toLowerCase();
  const fallback = detail || "publish failed";

  if (raw.kind === "push-rejected")
    return err("PushRejected", detail || "the remote moved ahead");

  if (/\b401\b|\b403\b|unauthor|forbidden|authentication/.test(haystack))
    return err("Unauthorized", fallback);

  if (/\b507\b|quota|storage full|insufficient storage|no space/.test(haystack))
    return err("QuotaExceeded", fallback);

  if (/timed out/.test(haystack)) return err("TimedOut", fallback);

  // "non-fast-forward" / "rejected" mean a concurrent publish won the race.
  if (/non-fast-forward|fetch first|rejected/.test(haystack))
    return err("PushRejected", fallback);

  if (/^clone exited|clone failed/.test(detail.toLowerCase()))
    return err("CloneFailed", fallback);

  if (/^(add|commit) exited/.test(detail.toLowerCase()))
    return err("CommitFailed", fallback);

  if (/^push exited/.test(detail.toLowerCase()))
    return err("PushRejected", fallback);

  if (/asset|upload/.test(haystack)) return err("AssetSyncFailed", fallback);

  return err("Unknown", fallback);
}

/** Turn an unexpected thrown value into the same shape. */
export function publishErrorFromThrown(e: unknown): PublishError {
  const detail = e instanceof Error ? e.message : String(e);
  return /timed out/i.test(detail)
    ? err("TimedOut", detail)
    : err("Unknown", detail);
}

/**
 * One sentence for the user, phrased so the next action is obvious. The raw
 * `detail` stays out of the headline — it belongs in diagnostics, not in a
 * red banner the author cannot act on.
 */
export function publishErrorMessage(e: PublishError): string {
  switch (e._tag) {
    case "Unauthorized":
      return "Your session expired — sign in again and retry the publish.";
    case "PushRejected":
      return "Someone published in the meantime — refresh and try again.";
    case "QuotaExceeded":
      return "You're out of storage — free up space or upgrade, then retry.";
    case "CloneFailed":
      return "Couldn't reach your site's repository — check your connection and retry.";
    case "CommitFailed":
      return "Couldn't record the changes locally. Retrying usually fixes it.";
    case "AssetSyncFailed":
      return "The pages published, but some images didn't upload — retry to finish them.";
    case "TimedOut":
      return "Publishing took too long and was stopped — retry.";
    case "InvalidSite":
      return `This site can't be published yet:\n${e.detail}`;
    case "Unknown":
      return "Publish failed. Retrying usually fixes it.";
  }
}

/** Whether re-running the same action is a sensible next step. */
export function isRetryable(e: PublishError): boolean {
  return (
    e._tag !== "Unauthorized" &&
    e._tag !== "QuotaExceeded" &&
    e._tag !== "InvalidSite"
  );
}

/**
 * A failed builder-api call during a publish (creating the site online), as
 * a sentence. Without this the dialog showed `ApiError`'s own message,
 * "api error 403", for a plan at its site limit: the server says why
 * (`{error: "max_sites_exceeded", limit, current}`), the author never saw it.
 */
export function apiErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const body = (e.body ?? {}) as { error?: unknown; limit?: unknown };
    if (e.status === 403 && body.error === "max_sites_exceeded")
      return `You've reached your plan's limit of ${String(body.limit)} sites. Delete one, or upgrade, to publish another.`;
    if (e.status === 401 || e.status === 403)
      return "Your session expired — sign in again and retry the publish.";
    return `Couldn't reach the builder (error ${e.status}). Retry in a moment.`;
  }
  if (e instanceof Error) return e.message;
  return "Publish failed. Retrying usually fixes it.";
}
